import React, { useState } from 'react';
import { FiLock } from 'react-icons/fi';
import { useTranslation } from '../../contexts/TranslationContext';

const DEFAULT_ACCENT = '#58C9FA';

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

interface ExpertPaidTierCardProps {
  name?: string | null;
  priceMonthly?: number | null;
  featuresDescription?: string | null;
  featuresList?: string[] | null;
  featured?: boolean | null;
  color?: string | null;
  activePicksCount?: number;
  onClick?: () => void;
  loading?: boolean;
  showFeatureList?: boolean;
}

const ExpertPaidTierCard: React.FC<ExpertPaidTierCardProps> = ({
  name,
  priceMonthly,
  featuresDescription,
  featuresList,
  featured,
  color,
  activePicksCount = 0,
  onClick,
  loading = false,
  showFeatureList = false,
}) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);

  const accent = color || DEFAULT_ACCENT;
  const accentSoft = hexToRgba(accent, 0.12);
  const accentGlow = hexToRgba(accent, 0.28);
  const lit = Boolean(featured);

  const priceDisplay = `$${priceMonthly ?? 0}`;

  const cardStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 0',
    minWidth: 0,
    background: lit
      ? `linear-gradient(160deg, ${accentSoft} 0%, var(--color-bg-card) 55%)`
      : 'var(--color-bg-card)',
    border: `1.5px solid ${lit ? accent : hovered ? 'var(--color-border)' : 'var(--color-border-subtle)'}`,
    borderRadius: 'var(--radius-xl)',
    padding: lit ? '26px 22px 22px' : '24px 22px 22px',
    boxShadow: lit ? `0 0 0 1px ${accentSoft}, 0 18px 40px -22px ${accentGlow}` : 'none',
    cursor: onClick ? 'pointer' : 'default',
    transition: 'border-color var(--transition-fast)',
    overflow: 'visible',
  };

  const features = featuresList && featuresList.length > 0 ? featuresList : null;

  return (
    <div
      style={cardStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      {lit && (
        <span style={{
          position: 'absolute',
          top: -13,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--color-bg)',
          background: accent,
          borderRadius: 'var(--radius-full)',
          padding: '5px 14px',
          whiteSpace: 'nowrap',
          boxShadow: `0 6px 18px -6px ${accentGlow}`,
        }}>
          ★ Most popular
        </span>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 6 }}>
        <span style={{
          width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
          background: accent,
          boxShadow: `0 0 12px ${accentGlow}`,
        }} />
        <span style={{
          fontSize: 13, fontWeight: 700,
          color: 'var(--color-text-primary)',
        }}>
          {name || '—'}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 14 }}>
        <span style={{
          fontSize: 38, fontWeight: 700, letterSpacing: '-0.035em',
          color: lit ? accent : 'var(--color-text-primary)',
          textShadow: lit ? `0 0 22px ${accentGlow}` : 'none',
        }}>
          {priceDisplay}
        </span>
        <span style={{
          fontSize: 12, fontWeight: 600,
          color: 'var(--color-text-secondary)',
          letterSpacing: '0.02em',
        }}>
          {t.monetization.access30Days}
        </span>
      </div>

      {featuresDescription && (
        <p style={{
          fontSize: 13, color: 'var(--color-text-secondary)',
          lineHeight: 1.65, margin: '0 0 18px',
        }}>
          {featuresDescription}
        </p>
      )}

      {showFeatureList && features && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 22 }}>
          {features.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--color-text-primary)' }}>
              <span style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 18, height: 18, borderRadius: '50%',
                background: accentSoft, flexShrink: 0,
                fontSize: 11, color: accent, fontWeight: 700,
              }}>
                ✓
              </span>
              {f}
            </div>
          ))}
        </div>
      )}

      {activePicksCount > 0 && (
        <p style={{
          fontSize: 12, fontWeight: 600, color: accent,
          marginBottom: 12,
          letterSpacing: '0.02em',
        }}>
          {activePicksCount} active {activePicksCount === 1 ? 'pick' : 'picks'}
        </p>
      )}

      {onClick && (
        <button
          onClick={(e) => { e.stopPropagation(); if (!loading) onClick(); }}
          disabled={loading}
          style={{
            marginTop: 'auto',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 7,
            background: lit ? accent : hovered ? accent : accentSoft,
            color: (lit || hovered) ? 'var(--color-bg)' : accent,
            border: 'none',
            borderRadius: 'var(--radius-md)',
            padding: '13px 0',
            fontSize: 13,
            fontWeight: 700,
            cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            opacity: loading ? 0.65 : 1,
            transition: 'background var(--transition-fast), color var(--transition-fast)',
          }}
        >
          {!loading && <FiLock size={14} color={(lit || hovered) ? 'var(--color-bg)' : accent} />}
          {loading ? '...' : t.expertProfile.buyAccess}
        </button>
      )}
    </div>
  );
};

export default ExpertPaidTierCard;
